import React, { useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { MdClose, MdSearch } from "react-icons/md";
import { getProgress } from "./tourStorage";

// The tour directory: every tour available to this login, with search, status and Start / Resume / Start again.

const EMERALD = "rgb(255, 209, 2)";
const ON = "#111827";

const statusOf = (progress, tour) => {
  const p = progress.tours[tour.id];
  const total = tour.steps.length;
  if (!p) return { key: "new", label: "Not started" };
  if (p.status === "completed") return { key: "done", label: "Completed" };
  return { key: "going", label: `In progress ${Math.min(p.currentStep + 1, total)}/${total}` };
};

const minutes = (tour) => Math.max(1, Math.round((tour.steps.length * 12) / 60));

export default function TourDirectory({ open, onClose, tours, roleName, onStart }) {
  const [q, setQ] = useState("");
  const progress = open ? getProgress() : { tours: {} };

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = tours.filter((t) => !t.hidden && (!needle || `${t.title} ${t.description} ${t.group}`.toLowerCase().includes(needle)));
    const map = new Map();
    list.forEach((t) => { if (!map.has(t.group)) map.set(t.group, []); map.get(t.group).push(t); });
    return [...map.entries()];
  }, [tours, q]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "18px" }, "aria-label": "Guided tours" }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Guided tours</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Available for your login: {roleName}</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close"><MdClose /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fafafa" }}>
        <TextField
          fullWidth size="small" placeholder="Search a page or module" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }}
          sx={{ mb: 2, bgcolor: "#fff", borderRadius: 1 }}
        />
        {groups.length === 0 && <Typography sx={{ py: 5, textAlign: "center", color: "#94a3b8" }}>No tours match your search.</Typography>}
        {groups.map(([group, list]) => (
          <Box key={group} sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a16207", mb: 0.75 }}>{group}</Typography>
            <Box sx={{ display: "grid", gap: 1 }}>
              {list.map((t) => {
                const st = statusOf(progress, t);
                return (
                  <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.5, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{t.title}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{t.description}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{t.steps.length} steps · about {minutes(t)} min</Typography>
                    </Box>
                    <Chip size="small" label={st.label} sx={{ fontWeight: 700, fontSize: 11, bgcolor: st.key === "done" ? "#DCFCE7" : st.key === "going" ? "#FEF3C7" : "#F1F5F9", color: st.key === "done" ? "#166534" : st.key === "going" ? "#92400E" : "#475569" }} />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {st.key === "going" && <Button size="small" variant="contained" disableElevation onClick={() => onStart(t.id, { resume: true })} sx={{ textTransform: "none", fontWeight: 700, bgcolor: EMERALD, color: ON, "&:hover": { bgcolor: "rgb(235, 192, 0)" } }}>Resume</Button>}
                      <Button size="small" variant={st.key === "going" ? "outlined" : "contained"} disableElevation onClick={() => onStart(t.id, { restart: true })} sx={{ textTransform: "none", fontWeight: 700, ...(st.key === "going" ? { color: ON, borderColor: EMERALD } : { bgcolor: EMERALD, color: ON, "&:hover": { bgcolor: "rgb(235, 192, 0)" } }) }}>
                        {st.key === "done" ? "Start again" : st.key === "going" ? "Restart" : "Start tour"}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}
